import React, { useEffect, useState } from 'react';
import AppNav from './AppNav';
import PriceChart from './PriceChart';
import { useRipple } from '../hooks/useRipple';
import './styles/clickEffects.css';

function useCountUp(target, duration = 1600) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!target) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(ease * target));
      if (p < 1) requestAnimationFrame(step);
    };
    const id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(id);
  }, [target, duration]);
  return val;
}

function useIsMobile(bp = 768) {
  const [m, setM] = React.useState(() => window.innerWidth <= bp);
  React.useEffect(() => {
    const h = () => setM(window.innerWidth <= bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return m;
}

export default function PredictionResult({
  data, onBack, onHome, onPricesClick,
  priceMode = 'student', onModeToggle, onNavigate,
  user = null, onUserChange,
}) {
  const {
    commodity, state, month, year,
    predicted_ngn, last_known_ngn,
    change_pct, trend, confidence,
    model_mape_pct, predicted_price, prev_month_price,
    pct_change, demo, source, rationale,
  } = data || {};

  const isMobile = useIsMobile();
  const isLive = priceMode === 'live';
  const triggerRipple = useRipple();

  const finalPrice  = predicted_ngn  || predicted_price  || 0;
  const prevPrice   = last_known_ngn || prev_month_price || 0;
  const finalPct    = change_pct !== undefined ? change_pct : (pct_change !== undefined ? parseFloat(pct_change) : 0);
  const finalTrend  = trend || (parseFloat(finalPct) > 3 ? 'rising' : parseFloat(finalPct) < -3 ? 'falling' : 'stable');
  const finalConf   = confidence || '–';
  const confPct     = model_mape_pct !== undefined ? (100 - Math.min(model_mape_pct, 50)).toFixed(1) : null;

  const animatedPrice = useCountUp(Math.round(finalPrice), 1800);

  const falling    = finalTrend === 'falling';
  const rising     = finalTrend === 'rising';
  const trendColor = falling ? '#2563eb' : rising ? '#dc2626' : '#424841';
  const trendBg    = falling ? 'rgba(37,99,235,0.1)' : rising ? 'rgba(220,38,38,0.1)' : 'rgba(66,72,65,0.1)';
  const trendLabel = falling ? 'Falling — good to buy now' : rising ? 'Rising — consider buying early' : 'Stable';

  const TrendIcon = ({ size = 16 }) => {
    if (falling) return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>;
    if (rising)  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>;
  };

  const projectedPrices = Array.from({ length: 6 }, (_, i) => {
    const drift = parseFloat(finalPct) / 100;
    return Math.round(finalPrice * Math.pow(1 + drift * 0.7, i));
  });

  const months      = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const monthNames  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const currentMonthIdx = typeof month === 'string' ? monthNames.indexOf(month) : (month - 1);
  const projLabels  = Array.from({ length: 6 }, (_, i) => months[(currentMonthIdx + i) % 12]);
  const maxProj     = Math.max(...projectedPrices);
  const minProj     = Math.min(...projectedPrices);

  const navH = isMobile ? 60 : 72;

  return (
    <div style={{ background: '#fcf9f8', minHeight: '100vh', fontFamily: 'Manrope, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Epilogue:wght@400;600;700;800;900&family=Manrope:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');
        @keyframes fadeInUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:none; } }
        @keyframes countIn  { from { opacity:0; transform:scale(0.88); } to { opacity:1; transform:scale(1); } }
        .pr-btn:hover { transform:translateY(-2px) !important; }
        .pr-btn:active { transform:translateY(0) !important; }
      `}</style>

      <AppNav
        activePage="result"
        priceMode={priceMode}
        onModeToggle={onModeToggle}
        onNavigate={onNavigate}
        isMobile={isMobile}
        user={user}
        onUserChange={onUserChange}
      />
      <div style={{ height: navH }} />

      <main style={{
        maxWidth: 860, margin: '0 auto',
        padding: 'clamp(1.25rem,4vw,2.5rem) clamp(0.9rem,4vw,1.5rem)',
        display: 'flex', flexDirection: 'column', gap: '1rem',
      }}>

        {/* ── Trend legend: always side by side, wrap on tiny screens ── */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div style={{
            flex: '1 1 140px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            padding: '7px 10px',
            background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.22)',
            fontSize: 11, fontWeight: 700, color: '#2563eb',
            fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '0.03em',
            whiteSpace: 'nowrap',
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>
            BLUE = FALLING = BUYERS
          </div>
          <div style={{
            flex: '1 1 140px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            padding: '7px 10px',
            background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.22)',
            fontSize: 11, fontWeight: 700, color: '#dc2626',
            fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '0.03em',
            whiteSpace: 'nowrap',
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
            RED = RISING = BUY EARLY
          </div>
        </div>

        {/* ── Main result card ── */}
        <div style={{
          background: isLive ? '#0d3321' : '#0a2e14',
          padding: 'clamp(1.25rem,4vw,2.25rem)', color: '#fff',
          position: 'relative', overflow: 'hidden',
          animation: 'fadeInUp 0.7s ease both',
          border: `1px solid ${isLive ? 'rgba(34,197,94,0.25)' : 'rgba(212,160,23,0.2)'}`,
          boxShadow: '6px 6px 0 0 rgba(10,46,20,0.15)',
          borderTop: `3px solid ${isLive ? '#22c55e' : '#d4a017'}`,
        }}>
          <div style={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: isLive ? 'rgba(34,197,94,0.06)' : 'rgba(212,160,23,0.08)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: '1.5rem' }}>
              <div>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.5)', fontWeight: 700, marginBottom: 7, fontFamily: 'Space Grotesk, sans-serif' }}>
                  Predicted Price For
                </div>
                <h2 style={{ fontFamily: 'Epilogue, sans-serif', fontSize: 'clamp(1.1rem,3vw,1.6rem)', fontWeight: 800, color: '#fff', marginBottom: 6, letterSpacing: '-0.02em' }}>
                  {commodity}
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: 'rgba(255,255,255,0.6)', fontFamily: 'Manrope, sans-serif', flexWrap: 'wrap' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    {state}
                  </span>
                  <span style={{ opacity: 0.4 }}>·</span>
                  <span>{month} {year}</span>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Space Grotesk, sans-serif' }}>Accuracy</div>
                <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 22, fontWeight: 700, color: isLive ? '#86efac' : '#d4a017' }}>
                  {confPct ? `${confPct}%` : finalConf}
                </div>
                {confPct && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}>confidence</div>}
              </div>
            </div>

            {/* Price + trend badge */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.45)', fontWeight: 700, marginBottom: 8, fontFamily: 'Space Grotesk, sans-serif' }}>
                Forecasted Market Price
              </div>
              {/* Stack on mobile, row on desktop — minHeight keeps surrounding layout stable */}
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: 10, minHeight: isMobile ? 80 : 'auto' }}>
                <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 'clamp(2.2rem,7vw,3.8rem)', fontWeight: 700, color: isLive ? '#86efac' : '#d4a017', lineHeight: 1, animation: 'countIn 0.8s ease both' }}>
                  ₦{animatedPrice.toLocaleString()}
                </span>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 12px', background: trendBg, color: trendColor, fontSize: 12, fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '0.03em', border: `1px solid ${trendColor}40`, whiteSpace: 'nowrap' }}>
                  <TrendIcon size={13} />
                  {parseFloat(finalPct) >= 0 ? '+' : ''}{parseFloat(finalPct).toFixed(2)}% · {trendLabel}
                </div>
              </div>
            </div>

            {/* Stats: vertical stack so text never breaks */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { label: 'Previous Price', val: prevPrice ? `₦${Math.round(prevPrice).toLocaleString()}` : '–' },
                { label: 'Price Change',   val: prevPrice ? `₦${Math.abs(Math.round(finalPrice - prevPrice)).toLocaleString()}` : '–' },
                { label: 'Confidence',     val: finalConf },
              ].map((s, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: 'rgba(255,255,255,0.06)', padding: '12px 14px',
                  borderTop: `1px solid ${isLive ? 'rgba(34,197,94,0.18)' : 'rgba(212,160,23,0.18)'}`,
                }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'Space Grotesk, sans-serif' }}>{s.label}</div>
                  <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 15, fontWeight: 700, color: '#fff' }}>{s.val}</div>
                </div>
              ))}
            </div>

            {/* Live rationale — minHeight prevents layout shift when toggling */}
            <div style={{ minHeight: isLive && rationale ? undefined : 0 }}>
              {isLive && rationale && (
                <div style={{ marginTop: '1rem', padding: '12px 14px', background: 'rgba(34,197,94,0.08)', borderLeft: '3px solid #22c55e', fontSize: 12, color: '#86efac', fontFamily: 'Manrope, sans-serif', lineHeight: 1.6 }}>
                  <strong>Analysis:</strong> {rationale}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── 6-Month Projection ── */}
        <div style={{
          background: '#ffffff', border: '1px solid rgba(10,46,20,0.1)',
          boxShadow: '6px 6px 0 0 rgba(10,46,20,0.06)',
          padding: 'clamp(1.1rem,4vw,1.75rem)',
          animation: 'fadeInUp 0.8s ease 0.15s both',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0a2e14" strokeWidth="2" strokeLinecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
            <div style={{ fontFamily: 'Epilogue, sans-serif', fontSize: 15, fontWeight: 700, color: '#001805' }}>6-Month Price Outlook</div>
          </div>
          <div style={{ fontSize: 12, color: '#424841', marginBottom: '1.1rem', display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', fontFamily: 'Manrope, sans-serif' }}>
            Projected trend — indicative only —
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: trendColor, fontWeight: 700 }}>
              <TrendIcon size={12} />
              {trendLabel}
            </span>
          </div>

          <svg viewBox="0 0 700 160" width="100%" style={{ display: 'block', overflow: 'visible' }}>
            {[0, 1, 2, 3].map(i => (
              <line key={i} x1="40" y1={20 + i * 30} x2="680" y2={20 + i * 30} stroke="#e5e2e1" strokeWidth="1" />
            ))}
            {projectedPrices.length > 1 && (() => {
              const pts = projectedPrices.map((v, i) => {
                const x = 40 + (i / (projectedPrices.length - 1)) * 640;
                const y = 20 + 90 * (1 - (v - minProj) / (maxProj - minProj + 1));
                return `${x},${y}`;
              });
              const first = pts[0].split(',');
              const last  = pts[pts.length - 1].split(',');
              return (
                <>
                  <defs>
                    <linearGradient id="prAreaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={trendColor} stopOpacity="0.2" />
                      <stop offset="100%" stopColor={trendColor} stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <polygon points={`${pts.join(' ')} ${last[0]},130 ${first[0]},130`} fill="url(#prAreaGrad)" />
                  <polyline points={pts.join(' ')} fill="none" stroke={trendColor} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                  {projectedPrices.map((v, i) => {
                    const [x, y] = pts[i].split(',');
                    return (
                      <g key={i}>
                        <circle cx={x} cy={y} r="4.5" fill={trendColor} />
                        <circle cx={x} cy={y} r="8" fill={trendColor} opacity="0.15" />
                        <text x={x} y={parseFloat(y) - 12} fontSize="9" fill="#424841" fontFamily="Space Grotesk, sans-serif" textAnchor="middle" fontWeight="600">
                          ₦{(v / 1000).toFixed(0)}k
                        </text>
                      </g>
                    );
                  })}
                </>
              );
            })()}
            {projLabels.map((l, i) => (
              <text key={l} x={40 + (i / (projLabels.length - 1)) * 640} y={152} fontSize="10" fill="#727970" fontFamily="Space Grotesk, sans-serif" textAnchor="middle" fontWeight="600">{l}</text>
            ))}
          </svg>

          <div style={{ marginTop: 10, padding: '11px 14px', background: falling ? '#eff6ff' : rising ? '#fef2f2' : '#f6f3f2', borderLeft: `4px solid ${trendColor}`, fontSize: 12, color: '#1c1b1b', display: 'flex', alignItems: 'flex-start', gap: 9, fontFamily: 'Manrope, sans-serif' }}>
            <span style={{ color: trendColor, marginTop: 1, flexShrink: 0 }}><TrendIcon size={13} /></span>
            <span>
              <strong>{commodity}</strong> in <strong>{state}</strong> projected to
              {falling ? ' decrease' : rising ? ' increase' : ' remain stable'} over 6 months.
              {falling ? ' Good time to buy.' : rising ? ' Consider buying early.' : ' Monitor regularly.'}
            </span>
          </div>
        </div>

        {/* ── Market Context Chart ── */}
        <div style={{
          background: '#ffffff', border: '1px solid rgba(10,46,20,0.1)',
          boxShadow: '6px 6px 0 0 rgba(10,46,20,0.06)',
          padding: 'clamp(1.1rem,4vw,1.75rem)',
          animation: 'fadeInUp 0.9s ease 0.25s both',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0a2e14" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
            <div style={{ fontFamily: 'Epilogue, sans-serif', fontSize: 15, fontWeight: 700, color: '#001805' }}>Market Context</div>
          </div>
          <div style={{ fontSize: 12, color: '#424841', marginBottom: '0.9rem', fontFamily: 'Manrope, sans-serif' }}>
            Price movement of key commodities for broader context
          </div>
          <PriceChart activeTab="1M" height={180} />
        </div>

        {/* ── Actions ── */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', animation: 'fadeInUp 1s ease 0.35s both' }}>
          <button 
            className="pr-btn ripple-host" 
            onClick={onBack} 
            onPointerDown={triggerRipple}
            style={{
              flex: '1 1 140px', height: 50,
              background: isLive ? '#166534' : '#0a2e14',
              color: isLive ? '#86efac' : '#d4a017',
              border: 'none', fontSize: 14, fontWeight: 800,
              cursor: 'pointer', fontFamily: 'Epilogue, sans-serif',
              transition: 'all 0.2s',
              boxShadow: '6px 6px 0 0 rgba(10,46,20,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
            New Prediction
          </button>
          <button 
            className="pr-btn ripple-host" 
            onClick={onHome}
            onPointerDown={triggerRipple}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,160,23,0.22)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(212,160,23,0.12)'}
            style={{
              flex: '1 1 140px', height: 50,
              background: 'rgba(212,160,23,0.12)', color: '#795900',
              border: '1px solid rgba(212,160,23,0.35)',
              fontSize: 14, fontWeight: 800, cursor: 'pointer',
              fontFamily: 'Epilogue, sans-serif', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            Dashboard
          </button>
          <button 
            className="pr-btn ripple-host"
            onClick={() => {
              const text = `NaijaAgro: ${commodity} in ${state} for ${month} ${year} — ₦${Math.round(finalPrice).toLocaleString()} (${parseFloat(finalPct) >= 0 ? '+' : ''}${parseFloat(finalPct).toFixed(2)}% — ${finalTrend})`;
              navigator.clipboard?.writeText(text);
              alert('Copied!');
            }}
            onPointerDown={triggerRipple}
            onMouseEnter={e => e.currentTarget.style.background = '#e5e2e1'}
            onMouseLeave={e => e.currentTarget.style.background = '#f6f3f2'}
            style={{
              flex: '1 1 140px', height: 50,
              background: '#f6f3f2', color: '#424841',
              border: '1px solid #c2c8bf',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'Manrope, sans-serif', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            Copy Result
          </button>
        </div>

      </main>
    </div>
  );
}